import React, { useMemo } from 'react';
import {
  Grid,
  Button,
  Typography,
  DialogContent,
  DialogActions
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { FormattedMessage, useIntl } from 'react-intl';
import classNames from 'classnames';
import { GroupTypeDetails } from 'graphql/types';
import { useScrollbarStyles } from 'modules/common';
import { SimpleDropDown } from './SimpleDropDown';
import { EnabledDisabled } from './constant';
import { GroupSelectionRow } from './GroupSelectionRow';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(0),
    alignContent: 'flex-start',
    whiteSpace: 'nowrap',
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  contentDialog: {
    padding: theme.spacing(1.5, 1, 1.5, 1),
    background: theme.palette.information.pageBackground
  },
  actions: {
    padding: theme.spacing(1.25, 4, 1.25, 1.25),
    display: 'flex',
    justifyContent: 'space-between',
    background: theme.palette.information.pageBackground
  },
  content: {
    display: 'flex',
    borderRadius: theme.spacing(0, 1.25, 0, 0),
    alignContent: 'flex-start'
  },
  dropDown: {
    marginRight: theme.spacing(1)
  },
  text: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  button: {
    marginLeft: theme.spacing(2),
    textTransform: 'none'
  },
  buttonText: {
    fontWeight: theme.typography.fontWeightMedium
  },
  actionButtonSpan: {
    display: 'flex'
  },
  submitSettingDialog: {
    display: 'flex',
    alignContent: 'flex-start',
    marginLeft: theme.spacing(1),
    marginTop: theme.spacing(0),
    padding: theme.spacing(0.75, 2)
  }
}));
type SettingBodyProps = {
  enabledGroupTypes: GroupTypeDetails[];
  fields: Record<string, any>[];
  handleRemove: (index?: number | number[] | undefined) => void;
  handleClick: () => void;
  isDirty: boolean;
  saving: boolean;
  fieldName: string;
  defaultRowValue: EnabledDisabled | undefined;
  onClose?: () => void;
  label?: string;
};

export const SettingBody: React.FC<SettingBodyProps> = ({
  enabledGroupTypes,
  fields,
  handleRemove,
  handleClick,
  isDirty,
  saving,
  fieldName,
  defaultRowValue,
  onClose,
  label
}: SettingBodyProps) => {
  const scrollBarStyles = useScrollbarStyles();
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const options = useMemo(() => {
    return [
      {
        key: EnabledDisabled.enabled,
        value: formatMessage({ id: 'settings.enabledDisabled.enabled' })
      },
      {
        key: EnabledDisabled.disabled,
        value: formatMessage({ id: 'settings.enabledDisabled.disabled' })
      }
    ];
  }, [formatMessage]);

  const defaultRow = useMemo(() => {
    return (
      <div className={classes.content}>
        <div className={classes.dropDown}>
          <SimpleDropDown
            value={defaultRowValue}
            options={options}
            rowID={'defaultRow'}
            label={label}
          />
        </div>
        <Typography display="inline" className={classes.text} variant="body2">
          <FormattedMessage id="settings.defaultValue" />
        </Typography>
        <Button
          className={classes.button}
          size="small"
          color="primary"
          onClick={handleClick}
          data-testid={'add-target-button'}
          disabled={!enabledGroupTypes || enabledGroupTypes!.length === 0}
        >
          <AddOutlinedIcon fontSize="inherit" />
          <Typography
            display="inline"
            className={classes.buttonText}
            variant="body2"
          >
            <FormattedMessage id="settings.addTargetButton" />
          </Typography>
        </Button>
      </div>
    );
  }, [
    classes.button,
    classes.buttonText,
    classes.content,
    classes.dropDown,
    classes.text,
    defaultRowValue,
    enabledGroupTypes,
    handleClick,
    label,
    options
  ]);

  const bodyContent = useMemo(() => {
    return (
      <div>
        {fields.map((field, index) => (
          <GroupSelectionRow
            key={field.id}
            options={options}
            enabledGroupTypes={enabledGroupTypes}
            rowID={`${fieldName}.${index}`}
            rowIndex={index}
            field={field}
            onRemove={handleRemove}
            label={label}
          />
        ))}
      </div>
    );
  }, [enabledGroupTypes, fieldName, fields, handleRemove, label, options]);

  const footerContent = useMemo(() => {
    return (
      <>
        {defaultRow}
        <span className={classes.actionButtonSpan}>
          <Button
            variant="text"
            onClick={onClose}
            data-testid="settings-cancel-button"
          >
            <Typography color="textSecondary" fontWeight={500}>
              <FormattedMessage id="settingsDialog.cancel" />
            </Typography>
          </Button>
          <Button
            className={classes.submitSettingDialog}
            disabled={saving || !isDirty}
            size={'medium'}
            color="primary"
            variant="contained"
            data-testid="save-settings-button"
            type="submit"
          >
            <FormattedMessage id="settings.save" />
          </Button>
        </span>
      </>
    );
  }, [
    defaultRow,
    classes.actionButtonSpan,
    classes.submitSettingDialog,
    onClose,
    saving,
    isDirty
  ]);

  return (
    <Grid
      className={classes.container}
      data-testid="setting-content-page-start-end"
    >
      <DialogContent
        className={classNames(classes.contentDialog, scrollBarStyles.scrollbar)}
      >
        {bodyContent}
      </DialogContent>
      <DialogActions className={classes.actions}>{footerContent}</DialogActions>
    </Grid>
  );
};
